import {
	Alert,
	Input,
	Space,
	Table,
	Tag,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FC } from 'react';
import { AUTH_SERVICES_BASE_URL } from '@constants/url';
import PageContainer from '@ui/PageContainer';
import PageTitle from '@ui/PageTitle';
import Section from '@ui/Section';
import useUsersPage, { type UserAssignment } from './useUsersPage';
import styles from './styles.module.scss';

const { Text, Title } = Typography;

/**
 * Filter options are the distinct values actually present in the column, so
 * the dropdown never offers something that would match nothing.
 */
const filtersFor = (items: UserAssignment[], key: keyof UserAssignment) =>
	[...new Set(items.map((item) => item[key]))].sort().map((value) => ({ text: value, value }));

const getColumns = (items: UserAssignment[]): ColumnsType<UserAssignment> => [
	{
		title: 'Object type',
		dataIndex: 'objectType',
		key: 'objectType',
		filters: filtersFor(items, 'objectType'),
		onFilter: (value, assignment) => assignment.objectType === value,
	},
	{
		title: 'Object ID',
		dataIndex: 'objectId',
		key: 'objectId',
		render: (objectId: string) => (
			<Space size={4}>
				{/* antd's copyable handles the clipboard and the copied state. */}
				<Text
					copyable={{ text: objectId, tooltips: ['Copy Object ID', 'Copied'] }}
					data-testid={`users-copy-${objectId}`}
				/>
				<Tag className={styles.objectId}>{objectId}</Tag>
			</Space>
		),
	},
	{
		title: 'Role',
		dataIndex: 'role',
		key: 'role',
		filters: filtersFor(items, 'role'),
		onFilter: (value, assignment) => assignment.role === value,
	},
];

const UsersPage: FC = () => {
	const {
		search,
		items,
		roles,
		isFetching,
		hasSearched,
	} = useUsersPage();

	return (
		<PageContainer>
			<PageTitle>Users</PageTitle>
			<Section white spacious>
				<Title
					level={3}
					className={styles.sectionTitle}
				>
					User assignments
				</Title>
				<Input.Search
					placeholder="Enter Client ID"
					enterButton="Search"
					allowClear
					loading={isFetching}
					disabled={!AUTH_SERVICES_BASE_URL}
					onSearch={search}
					data-testid="users-cid"
				/>
				{roles.length > 0 && (
					<Alert
						type="info"
						showIcon
						className={styles.rolesAlert}
						message="High level permissions"
						description={
							<Space
								wrap
								size={4}
								data-testid="users-roles"
							>
								{roles.map((role) => (
									<Tag
										key={role}
										className={styles.roleTag}
									>
										{role}
									</Tag>
								))}
							</Space>
						}
					/>
				)}
				{items.length > 0 && (
					<Table<UserAssignment>
						size="small"
						columns={getColumns(items)}
						dataSource={items}
						pagination={false}
						// No id on a row, so identify it by what it is.
						rowKey={({ objectType, objectId, role }) => `${objectType}-${objectId}-${role}`}
						data-testid="users-results"
					/>
				)}
				{/* The table is hidden when empty, so a search that found nothing
				    still needs to say so. */}
				{hasSearched && !isFetching && !items.length && !roles.length && (
					<Text
						type="secondary"
						data-testid="users-empty"
					>
						No assignments found
					</Text>
				)}
			</Section>
		</PageContainer>
	);
};

export default UsersPage;
